"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { CandlePatternInfo, getCandlePatternInfo } from "@/lib/candlePatternInfo";
import { CandleSetupGroup, DataItem, DojiContext } from "@/types";
import KnowledgeCentre from "../KnowledgeCentre";
import { formatDate, formatNumber, formatPatternName } from "./utils";

type SignalWorkspaceMode = "patterns" | "structure" | "reversal";
type TrendFilter = "all" | "up" | "down";
type SignalDirection = "Bullish" | "Bearish" | "Neutral";
type ResultTableVariant = "default" | "doji";

interface SignalWorkspaceProps {
  title: string;
  description: string;
  items: DataItem[];
  uniqueTypes: string[];
  setupGroups?: CandleSetupGroup[];
  selectedType: string | null;
  onSelectType: (type: string | null) => void;
  selectedPatternInfo: CandlePatternInfo | null;
  trendFilter: TrendFilter;
  setTrendFilter: (filter: TrendFilter) => void;
  timeframe?: "daily" | "weekly" | "monthly";
  mode: SignalWorkspaceMode;
  emptyMessage: string;
  tableVariant?: ResultTableVariant;
}

type CanonicalCard = {
  canonicalId: string;
  displayName: string;
  rawTypes: string[];
  count: number;
  direction: SignalDirection;
  signalClass: "setup" | "context" | "noise";
  reliability?: CandlePatternInfo["reliability"];
  description?: string;
  upCount: number;
  downCount: number;
  info: CandlePatternInfo | null;
};

const directionAccent: Record<SignalDirection, string> = {
  Bullish: "#10b981",
  Bearish: "#ef4444",
  Neutral: "#8b95a5",
};

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

function useCompactViewport() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 720px)");
    const updateViewport = () => setIsCompact(mediaQuery.matches);

    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  return isCompact;
}

const inferSignalClass = (rawType: string): "setup" | "context" | "noise" => {
  const normalized = String(rawType || "").trim().toUpperCase();
  if (normalized === "DOJI" || normalized === "SPINNING_TOP") return "context";
  if (normalized === "NORMAL_BULL" || normalized === "NORMAL_BEAR" || normalized === "UNKNOWN") return "noise";
  return "setup";
};

const normalizeTypeList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const cleaned = value.trim();
    return cleaned ? [cleaned] : [];
  }
  return [];
};

const getModeTypes = (item: DataItem, mode: SignalWorkspaceMode): string[] => {
  if (mode === "patterns") {
    const raw = String(item.candle_type || item.type || "").trim();
    return raw ? [raw] : [];
  }
  if (mode === "structure") {
    return normalizeTypeList(item.structure);
  }
  return normalizeTypeList(item.reversal);
};

const getTrendLabel = (trend?: string) => {
  if (trend === "UPTREND") return "Uptrend";
  if (trend === "DOWNTREND") return "Downtrend";
  return "Neutral";
};

const getDateLabel = (item: DataItem, timeframe: "daily" | "weekly" | "monthly") => {
  if (timeframe === "weekly" && typeof item.week_label === "string" && item.week_label.trim()) {
    return item.week_label;
  }
  if (timeframe === "monthly" && typeof item.month_label === "string" && item.month_label.trim()) {
    return item.month_label;
  }
  return formatDate(typeof item.date === "string" ? item.date : undefined) || "Latest scan";
};

const buildContextText = (item: DataItem, mode: SignalWorkspaceMode) => {
  const structureTags = normalizeTypeList(item.structure);
  const reversalTags = normalizeTypeList(item.reversal);
  const continuationTags = normalizeTypeList(item.continuation);
  const snippets: string[] = [];

  if (mode !== "patterns") {
    const candle = item.candle_type || item.type;
    if (typeof candle === "string" && candle.trim()) {
      snippets.push(`Candle: ${formatPatternName(candle)}`);
    }
  }
  if (mode !== "structure" && structureTags.length > 0) {
    snippets.push(`Structure: ${structureTags.map((tag) => formatPatternName(tag)).join(", ")}`);
  }
  if (mode !== "reversal" && reversalTags.length > 0) {
    snippets.push(`Reversal: ${reversalTags.map((tag) => formatPatternName(tag)).join(", ")}`);
  }
  if (continuationTags.length > 0) {
    snippets.push(`Continuation: ${continuationTags.map((tag) => formatPatternName(tag)).join(", ")}`);
  }

  return snippets.join(" · ") || "Standalone signal";
};

const getGroupTitle = (direction: SignalDirection) => {
  if (direction === "Bullish") return "Bullish Bias";
  if (direction === "Bearish") return "Bearish Bias";
  return "Neutral / Mixed";
};

const getDirectionFromTrendCounts = (upCount: number, downCount: number): SignalDirection => {
  if (upCount > downCount) return "Bullish";
  if (downCount > upCount) return "Bearish";
  return "Neutral";
};

const formatCompactNumber = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return compactNumberFormatter.format(value);
};

const getDojiContext = (item: DataItem): DojiContext | null => {
  const value = item.doji_context;
  return typeof value === "object" && value !== null ? (value as DojiContext) : null;
};

export default function SignalWorkspace({
  title,
  description,
  items,
  uniqueTypes,
  setupGroups,
  selectedType,
  onSelectType,
  selectedPatternInfo,
  trendFilter,
  setTrendFilter,
  timeframe = "daily",
  mode,
  emptyMessage,
  tableVariant = "default",
}: SignalWorkspaceProps) {
  const isCompact = useCompactViewport();

  const [showSecondarySignals, setShowSecondarySignals] = useState(false);

  const setupCards = useMemo(() => {
    if (setupGroups?.length) {
      return setupGroups.map((group) => {
        const info = getCandlePatternInfo(group.key) || getCandlePatternInfo(group.raw_types[0]);
        return {
          canonicalId: group.key,
          displayName: group.display_name,
          rawTypes: group.raw_types,
          count: group.count,
          direction: group.direction,
          signalClass: group.signal_class || inferSignalClass(group.key),
          reliability: group.reliability || info?.reliability,
          description: info?.description,
          upCount: group.uptrend_count,
          downCount: group.downtrend_count,
          info,
        };
      });
    }

    const rawCards = uniqueTypes
      .map((rawType) => {
        const typeItems = items.filter((item) => getModeTypes(item, mode).includes(rawType));
        if (typeItems.length === 0) return null;
        const info = getCandlePatternInfo(rawType);
        return {
          rawType,
          displayName: info?.displayName || formatPatternName(rawType),
          count: typeItems.length,
          upCount: typeItems.filter((item) => item.trend === "UPTREND").length,
          downCount: typeItems.filter((item) => item.trend === "DOWNTREND").length,
          signalClass: inferSignalClass(rawType),
          info,
        };
      })
      .filter(Boolean) as Array<{
      rawType: string;
      displayName: string;
      count: number;
      upCount: number;
      downCount: number;
      signalClass: "setup" | "context" | "noise";
      info: CandlePatternInfo | null;
    }>;

    const canonicalMap = new Map<string, CanonicalCard>();

    rawCards.forEach((entry) => {
      const key = entry.displayName.toUpperCase();
      const existing = canonicalMap.get(key);
      const direction = entry.info?.type || getDirectionFromTrendCounts(entry.upCount, entry.downCount);

      if (existing) {
        existing.rawTypes.push(entry.rawType);
        existing.count += entry.count;
        existing.upCount += entry.upCount;
        existing.downCount += entry.downCount;
        if (existing.signalClass !== "setup") existing.signalClass = entry.signalClass;
        if (!existing.info && entry.info) existing.info = entry.info;
        if (!existing.reliability && entry.info?.reliability) existing.reliability = entry.info.reliability;
        if (!existing.description && entry.info?.description) existing.description = entry.info.description;
        if (existing.direction === "Neutral") existing.direction = direction;
        return;
      }

      canonicalMap.set(key, {
        canonicalId: key,
        displayName: entry.displayName,
        rawTypes: [entry.rawType],
        count: entry.count,
        direction,
        signalClass: entry.signalClass,
        reliability: entry.info?.reliability,
        description: entry.info?.description,
        upCount: entry.upCount,
        downCount: entry.downCount,
        info: entry.info,
      });
    });

    return Array.from(canonicalMap.values()).sort((a, b) => b.count - a.count);
  }, [items, mode, setupGroups, uniqueTypes]);

  const selectedCard = useMemo(
    () => setupCards.find((card) => selectedType && card.rawTypes.includes(selectedType)) || null,
    [selectedType, setupCards],
  );

  const filteredRows = useMemo(() => {
    if (!selectedCard) return [];
    return items.filter((item) => {
      const matchesSetup = getModeTypes(item, mode).some((value) => selectedCard.rawTypes.includes(value));
      const matchesTrend =
        trendFilter === "all" ||
        (trendFilter === "up" && item.trend === "UPTREND") ||
        (trendFilter === "down" && item.trend === "DOWNTREND");
      return matchesSetup && matchesTrend;
    });
  }, [items, mode, selectedCard, trendFilter]);

  const groupedCards = useMemo(() => {
    if (mode === "patterns") {
      return {
        bullishSetups: setupCards.filter((card) => card.signalClass === "setup" && card.direction === "Bullish"),
        bearishSetups: setupCards.filter((card) => card.signalClass === "setup" && card.direction === "Bearish"),
        neutralSetups: setupCards.filter((card) => card.signalClass === "setup" && card.direction === "Neutral"),
        contextSignals: setupCards.filter((card) => card.signalClass === "context"),
        noiseSignals: setupCards.filter((card) => card.signalClass === "noise"),
      };
    }

    const directionalBuckets: Record<SignalDirection, CanonicalCard[]> = {
      Bullish: [],
      Bearish: [],
      Neutral: [],
    };
    setupCards.forEach((card) => {
      directionalBuckets[card.direction].push(card);
    });
    return directionalBuckets;
  }, [mode, setupCards]);

  const uptrendCount = useMemo(() => items.filter((item) => item.trend === "UPTREND").length, [items]);
  const downtrendCount = useMemo(() => items.filter((item) => item.trend === "DOWNTREND").length, [items]);
  const actionableCards = useMemo(
    () => (mode === "patterns" ? setupCards.filter((card) => card.signalClass === "setup") : setupCards),
    [mode, setupCards],
  );
  const actionableHitCount = useMemo(
    () => actionableCards.reduce((total, card) => total + card.count, 0),
    [actionableCards],
  );
  const topSetups = useMemo(() => {
    return actionableCards.slice(0, 3).map((card) => `${card.displayName} (${card.count})`).join(" · ");
  }, [actionableCards]);
  const activeInfo = selectedCard?.info || selectedPatternInfo || null;
  const patternBuckets =
    mode === "patterns"
      ? (groupedCards as {
          bullishSetups: CanonicalCard[];
          bearishSetups: CanonicalCard[];
          neutralSetups: CanonicalCard[];
          contextSignals: CanonicalCard[];
          noiseSignals: CanonicalCard[];
        })
      : null;

  const renderCardList = (cards: CanonicalCard[]) => (
    <div style={{ display: "grid", gap: "0.55rem" }}>
      {cards.map((card) => {
        const isSelected = !!selectedCard && selectedCard.canonicalId === card.canonicalId;
        const dominantTrend =
          card.upCount > card.downCount
            ? `Uptrend fit ${card.upCount}`
            : card.downCount > card.upCount
            ? `Downtrend fit ${card.downCount}`
            : "Mixed trend";
        return (
          <button
            key={card.canonicalId}
            type="button"
            onClick={() => onSelectType(isSelected ? null : card.rawTypes[0])}
            style={{
              padding: "0.85rem 0.9rem",
              borderRadius: "14px",
              border: isSelected
                ? `1px solid ${directionAccent[card.direction]}`
                : "1px solid rgba(148, 163, 184, 0.18)",
              background: isSelected
                ? "linear-gradient(135deg, rgba(30, 64, 175, 0.22), rgba(15, 23, 42, 0.8))"
                : "rgba(15, 23, 42, 0.54)",
              color: "var(--color-text)",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.3rem" }}>
              <div style={{ fontWeight: 700 }}>{card.displayName}</div>
              <div style={{ color: directionAccent[card.direction], fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{card.count}</div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.38rem" }}>
              <span
                style={{
                  padding: "0.18rem 0.48rem",
                  borderRadius: "999px",
                  background: "rgba(15, 23, 42, 0.78)",
                  border: `1px solid rgba(148, 163, 184, ${isSelected ? "0.34" : "0.18"})`,
                  color: directionAccent[card.direction],
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                {card.direction}
              </span>
              {card.reliability && (
                <span
                  style={{
                    padding: "0.18rem 0.48rem",
                    borderRadius: "999px",
                    background: "rgba(15, 23, 42, 0.78)",
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    color: "var(--color-text)",
                    fontSize: "0.72rem",
                  }}
                >
                  {card.reliability} reliability
                </span>
              )}
              {mode === "patterns" && card.signalClass !== "setup" && (
                <span
                  style={{
                    padding: "0.18rem 0.48rem",
                    borderRadius: "999px",
                    background: "rgba(15, 23, 42, 0.78)",
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    color: card.signalClass === "noise" ? "#94a3b8" : "#fbbf24",
                    fontSize: "0.72rem",
                  }}
                >
                  {card.signalClass === "noise" ? "Basic candle" : "Context signal"}
                </span>
              )}
            </div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", lineHeight: 1.5 }}>
              {dominantTrend}
              {card.rawTypes.length > 1 ? ` · merged aliases ${card.rawTypes.length}` : ""}
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: "grid", gap: "1.35rem", animation: "fadeIn 0.3s ease" }}>
      <div
        style={{
          background: "linear-gradient(145deg, rgba(15, 23, 42, 0.86), rgba(15, 23, 42, 0.68) 60%, rgba(30, 41, 59, 0.48))",
          border: "1px solid rgba(59, 130, 246, 0.16)",
          borderRadius: "18px",
          padding: "1.35rem 1.4rem",
          boxShadow: "0 18px 38px rgba(2, 6, 23, 0.22)",
        }}
      >
        <div style={{ color: "#93c5fd", fontSize: "0.76rem", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "0.55rem" }}>
          Pattern Workspace
        </div>
        <div style={{ display: "grid", gap: "0.55rem" }}>
          <h2 style={{ color: "var(--color-text)", fontSize: "1.6rem", margin: 0 }}>{title}</h2>
          <p style={{ color: "rgba(241, 245, 249, 0.78)", margin: 0, lineHeight: 1.65, maxWidth: "54rem" }}>
            {description}
          </p>
          <p style={{ color: "var(--color-text-muted)", margin: 0, lineHeight: 1.6 }}>
            {topSetups ? `Most active today: ${topSetups}.` : "No active setups available right now."}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.85rem", marginTop: "1.15rem" }}>
          <div style={{ background: "rgba(15, 23, 42, 0.62)", borderRadius: "14px", border: "1px solid rgba(148, 163, 184, 0.16)", padding: "0.95rem" }}>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginBottom: "0.28rem" }}>Signal Hits</div>
            <div style={{ fontSize: "1.28rem", fontWeight: 700, color: "var(--color-text)" }}>
              {mode === "patterns" ? actionableHitCount : items.length}
            </div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginTop: "0.25rem" }}>
              {mode === "patterns" ? "Actionable setup matches" : "Current timeframe matches"}
            </div>
          </div>
          <div style={{ background: "rgba(15, 23, 42, 0.62)", borderRadius: "14px", border: "1px solid rgba(148, 163, 184, 0.16)", padding: "0.95rem" }}>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginBottom: "0.28rem" }}>Setup Families</div>
            <div style={{ fontSize: "1.28rem", fontWeight: 700, color: "var(--color-text)" }}>
              {mode === "patterns" ? actionableCards.length : setupCards.length}
            </div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginTop: "0.25rem" }}>
              {mode === "patterns" ? "Bullish and bearish setup families" : "Grouped by live pattern family"}
            </div>
          </div>
          <div style={{ background: "rgba(15, 23, 42, 0.62)", borderRadius: "14px", border: "1px solid rgba(148, 163, 184, 0.16)", padding: "0.95rem" }}>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginBottom: "0.28rem" }}>Uptrend Context</div>
            <div style={{ fontSize: "1.28rem", fontWeight: 700, color: "#10b981" }}>{uptrendCount}</div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginTop: "0.25rem" }}>Names already in uptrend</div>
          </div>
          <div style={{ background: "rgba(15, 23, 42, 0.62)", borderRadius: "14px", border: "1px solid rgba(148, 163, 184, 0.16)", padding: "0.95rem" }}>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginBottom: "0.28rem" }}>Downtrend Context</div>
            <div style={{ fontSize: "1.28rem", fontWeight: 700, color: "#ef4444" }}>{downtrendCount}</div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", marginTop: "0.25rem" }}>Names still in downtrend</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(280px, 340px) minmax(0, 1fr)", gap: "1rem", alignItems: "start", minWidth: 0 }}>
        <div
          style={{
            background: "rgba(15, 23, 42, 0.62)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            borderRadius: isCompact ? "14px" : "18px",
            padding: isCompact ? "0.9rem" : "1rem",
            display: "grid",
            gap: "1rem",
            minWidth: 0,
          }}
        >
          <div>
            <div style={{ color: "#93c5fd", fontSize: "0.74rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.38rem" }}>
              Setup Explorer
            </div>
            <div style={{ color: "var(--color-text)", fontSize: "1.06rem", fontWeight: 700, marginBottom: "0.35rem" }}>
              Select the setup family first
            </div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "0.84rem", lineHeight: 1.6 }}>
              {mode === "patterns"
                ? "High-conviction bullish and bearish setups are separated from indecision candles and basic bars so the real signals stay on top."
                : "Families are grouped by directional bias and sorted by live hit count so the most relevant setups appear first."}
            </div>
          </div>

          {mode === "patterns" && patternBuckets ? (
            <>
              {[
                { key: "bullishSetups", title: "Bullish Setups", accent: "#10b981" },
                { key: "bearishSetups", title: "Bearish Setups", accent: "#ef4444" },
                { key: "neutralSetups", title: "Neutral Setups", accent: "#8b95a5" },
              ].map((section) => {
                const cards = patternBuckets[section.key as keyof typeof patternBuckets];
                if (!cards.length) return null;
                return (
                  <div key={section.key} style={{ display: "grid", gap: "0.6rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                      <div style={{ fontWeight: 700, color: section.accent }}>{section.title}</div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>{cards.length} families</div>
                    </div>
                    {renderCardList(cards)}
                  </div>
                );
              })}

              {(patternBuckets.contextSignals.length > 0 || patternBuckets.noiseSignals.length > 0) && (
                <div style={{ display: "grid", gap: "0.65rem", paddingTop: "0.15rem" }}>
                  <button
                    type="button"
                    onClick={() => setShowSecondarySignals((value) => !value)}
                    style={{
                      padding: "0.65rem 0.85rem",
                      borderRadius: "12px",
                      background: "rgba(15, 23, 42, 0.48)",
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      color: "var(--color-text)",
                      fontWeight: 700,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {showSecondarySignals ? "Hide context and noise" : "Show context and noise"}
                  </button>

                  {showSecondarySignals && (
                    <>
                      {patternBuckets.contextSignals.length > 0 && (
                        <div style={{ display: "grid", gap: "0.6rem" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                            <div style={{ fontWeight: 700, color: "#fbbf24" }}>Context Signals</div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>{patternBuckets.contextSignals.length} families</div>
                          </div>
                          {renderCardList(patternBuckets.contextSignals)}
                        </div>
                      )}

                      {patternBuckets.noiseSignals.length > 0 && (
                        <div style={{ display: "grid", gap: "0.6rem" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                            <div style={{ fontWeight: 700, color: "#94a3b8" }}>Noise / Basic Candles</div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>{patternBuckets.noiseSignals.length} families</div>
                          </div>
                          {renderCardList(patternBuckets.noiseSignals)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            (["Bullish", "Bearish", "Neutral"] as const).map((direction) => {
              const cards = (groupedCards as Record<SignalDirection, CanonicalCard[]>)[direction];
              if (!cards.length) return null;
              return (
                <div key={direction} style={{ display: "grid", gap: "0.6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                    <div style={{ fontWeight: 700, color: directionAccent[direction] }}>{getGroupTitle(direction)}</div>
                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>{cards.length} families</div>
                  </div>
                  {renderCardList(cards)}
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: "grid", gap: "1rem" }}>
          <div
            style={{
              background: "rgba(15, 23, 42, 0.62)",
              border: "1px solid rgba(148, 163, 184, 0.16)",
              borderRadius: "18px",
              padding: "1rem 1.05rem",
            }}
          >
            {selectedCard ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                  <div>
                    <div style={{ color: "#93c5fd", fontSize: "0.74rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.35rem" }}>
                      Selected Setup
                    </div>
                    <h3 style={{ color: "var(--color-text)", fontSize: "1.22rem", margin: 0 }}>{selectedCard.displayName}</h3>
                    <p style={{ color: "var(--color-text-muted)", fontSize: "0.88rem", lineHeight: 1.6, margin: "0.45rem 0 0" }}>
                      {activeInfo?.description || `${selectedCard.displayName} currently has ${filteredRows.length} matches in the ${timeframe} scan.`}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignContent: "start" }}>
                    <span
                      style={{
                        padding: "0.3rem 0.65rem",
                        borderRadius: "999px",
                        background: "rgba(15, 23, 42, 0.82)",
                        border: `1px solid ${directionAccent[selectedCard.direction]}`,
                        color: directionAccent[selectedCard.direction],
                        fontSize: "0.78rem",
                        fontWeight: 700,
                      }}
                    >
                      {selectedCard.direction}
                    </span>
                    {activeInfo?.reliability && (
                      <span
                        style={{
                          padding: "0.3rem 0.65rem",
                          borderRadius: "999px",
                          background: "rgba(15, 23, 42, 0.82)",
                          border: "1px solid rgba(148, 163, 184, 0.22)",
                          color: "var(--color-text)",
                          fontSize: "0.78rem",
                        }}
                      >
                        {activeInfo.reliability} reliability
                      </span>
                    )}
                    <span
                      style={{
                        padding: "0.3rem 0.65rem",
                        borderRadius: "999px",
                        background: "rgba(15, 23, 42, 0.82)",
                        border: "1px solid rgba(148, 163, 184, 0.22)",
                        color: "var(--color-text)",
                        fontSize: "0.78rem",
                      }}
                    >
                      {timeframe}
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.85rem" }}>
                  {[
                    { label: "All Contexts", value: "all" as const },
                    { label: "Uptrend Fit", value: "up" as const, accent: "#10b981" },
                    { label: "Downtrend Fit", value: "down" as const, accent: "#ef4444" },
                  ].map((option) => {
                    const active = trendFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setTrendFilter(option.value)}
                        style={{
                          padding: "0.52rem 0.95rem",
                          borderRadius: "999px",
                          background: active ? `${option.accent || "#3b82f6"}22` : "rgba(15, 23, 42, 0.72)",
                          border: `1px solid ${active ? option.accent || "#3b82f6" : "rgba(148, 163, 184, 0.18)"}`,
                          color: active ? option.accent || "#dbeafe" : "var(--color-text)",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>

                <div style={{ color: "var(--color-text-muted)", fontSize: "0.82rem", marginBottom: "0.7rem" }}>
                  {filteredRows.length} matching names in the current filter.
                </div>

                {filteredRows.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.18)" }}>
                          <th style={{ padding: "0.72rem 0.55rem 0.72rem 0", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>Symbol</th>
                          <th style={{ padding: "0.72rem 0.55rem", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>Trend</th>
                          {tableVariant === "doji" && (
                            <th style={{ padding: "0.72rem 0.55rem", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>
                              MA Support / Resistance
                            </th>
                          )}
                          {tableVariant === "doji" && (
                            <th style={{ padding: "0.72rem 0.55rem", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>
                              Volume
                            </th>
                          )}
                          <th style={{ padding: "0.72rem 0.55rem", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>Detected</th>
                          <th style={{ padding: "0.72rem 0 0.72rem 0.55rem", textAlign: "left", color: "var(--color-text-muted)", fontSize: "0.76rem", fontWeight: 600 }}>Context</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((item, idx) => {
                          const trend = typeof item.trend === "string" ? item.trend : "";
                          const trendAccent =
                            trend === "UPTREND" ? "#10b981" : trend === "DOWNTREND" ? "#ef4444" : "#8b95a5";
                          const dojiContext = tableVariant === "doji" ? getDojiContext(item) : null;
                          const levelAccent =
                            dojiContext?.level_role === "support"
                              ? "#10b981"
                              : dojiContext?.level_role === "resistance"
                              ? "#ef4444"
                              : "#94a3b8";
                          const priceLabel = formatNumber(
                            typeof dojiContext?.ma_value === "number" ? dojiContext.ma_value : undefined,
                            2,
                          );
                          const rvolLabel = formatNumber(
                            typeof dojiContext?.rvol === "number" ? dojiContext.rvol : undefined,
                            2,
                          );
                          const pressureLabel =
                            dojiContext?.pressure_side === "selling"
                              ? "selling faded"
                              : dojiContext?.pressure_side === "buying"
                              ? "buying faded"
                              : null;
                          return (
                            <tr key={`${item.symbol || "row"}-${idx}`} style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.08)" }}>
                              <td style={{ padding: "0.8rem 0.55rem 0.8rem 0", verticalAlign: "top" }}>
                                {item.symbol ? (
                                  <Link href={`/stock/${encodeURIComponent(item.symbol)}`} style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 700 }}>
                                    {item.symbol}
                                  </Link>
                                ) : (
                                  <span style={{ color: "var(--color-text-muted)" }}>—</span>
                                )}
                              </td>
                              <td style={{ padding: "0.8rem 0.55rem", verticalAlign: "top" }}>
                                <span
                                  style={{
                                    padding: "0.22rem 0.5rem",
                                    borderRadius: "999px",
                                    background: "rgba(15, 23, 42, 0.75)",
                                    border: `1px solid ${trendAccent}`,
                                    color: trendAccent,
                                    fontSize: "0.74rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {getTrendLabel(trend)}
                                </span>
                              </td>
                              {tableVariant === "doji" && (
                                <td style={{ padding: "0.8rem 0.55rem", verticalAlign: "top" }}>
                                  {dojiContext ? (
                                    <div style={{ display: "grid", gap: "0.28rem" }}>
                                      <span
                                        style={{
                                          width: "fit-content",
                                          padding: "0.2rem 0.48rem",
                                          borderRadius: "999px",
                                          background: "rgba(15, 23, 42, 0.75)",
                                          border: `1px solid ${levelAccent}`,
                                          color: levelAccent,
                                          fontSize: "0.72rem",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {dojiContext.level_role === "support" ? "Support" : "Resistance"}
                                      </span>
                                      <div style={{ color: "var(--color-text)", fontSize: "0.84rem", fontWeight: 600 }}>
                                        EMA{dojiContext.ma_period ?? "—"}
                                      </div>
                                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem" }}>
                                        {priceLabel ? `@ ${priceLabel}` : "MA value unavailable"}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem", lineHeight: 1.5 }}>
                                      No MA confluence
                                    </div>
                                  )}
                                </td>
                              )}
                              {tableVariant === "doji" && (
                                <td style={{ padding: "0.8rem 0.55rem", verticalAlign: "top" }}>
                                  {dojiContext ? (
                                    <div style={{ display: "grid", gap: "0.2rem" }}>
                                      <div style={{ color: "var(--color-text)", fontSize: "0.84rem", fontWeight: 700 }}>
                                        {formatCompactNumber(
                                          typeof dojiContext.doji_volume === "number" ? dojiContext.doji_volume : undefined,
                                        )}
                                      </div>
                                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.76rem", lineHeight: 1.5 }}>
                                        {rvolLabel ? `RVOL ${rvolLabel}x` : "RVOL —"}
                                        {pressureLabel ? ` · ${pressureLabel}` : ""}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem", lineHeight: 1.5 }}>
                                      No volume trigger
                                    </div>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: "0.8rem 0.55rem", verticalAlign: "top", color: "var(--color-text)", fontSize: "0.84rem" }}>
                                {getDateLabel(item, timeframe)}
                              </td>
                              <td style={{ padding: "0.8rem 0 0.8rem 0.55rem", verticalAlign: "top", color: "var(--color-text-muted)", fontSize: "0.84rem", lineHeight: 1.6 }}>
                                {buildContextText(item, mode)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "2rem 1.25rem",
                      borderRadius: "14px",
                      background: "rgba(15, 23, 42, 0.48)",
                      border: "1px dashed rgba(148, 163, 184, 0.18)",
                      color: "var(--color-text-muted)",
                      textAlign: "center",
                    }}
                  >
                    No matches remain after the current trend filter.
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  minHeight: "22rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2rem 1.5rem",
                  borderRadius: "14px",
                  background: "rgba(15, 23, 42, 0.48)",
                  border: "1px dashed rgba(148, 163, 184, 0.2)",
                  textAlign: "center",
                }}
              >
                <div>
                  <div style={{ color: "var(--color-text)", fontSize: "1rem", fontWeight: 700, marginBottom: "0.35rem" }}>
                    Choose a setup family
                  </div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", lineHeight: 1.6, maxWidth: "28rem" }}>
                    {emptyMessage}
                  </div>
                </div>
              </div>
            )}
          </div>

          {activeInfo && (
            <div
              style={{
                background: "rgba(15, 23, 42, 0.62)",
                border: "1px solid rgba(148, 163, 184, 0.16)",
                borderRadius: "18px",
                padding: "1rem 1.1rem",
              }}
            >
              <div style={{ color: "#93c5fd", fontSize: "0.74rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.55rem" }}>
                Knowledge Panel
              </div>
              <KnowledgeCentre pattern={activeInfo} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
